<template>
    <div class="matches">
        <div class="title-wrapper">
            <span class="bracket-wrapper">
                <img class="bracket" src="~@/assets/celtic_bracket.png" />
            </span>
            <h1 class="title">
                {{ renderPageTitle() }}
            </h1>
            <span class="bracket-wrapper left">
                <img class="bracket flipped" src="~@/assets/celtic_bracket.png" />
            </span>
        </div>
        <div v-if="metadata && metadata.disqualified">
            <disqualified-alert type="banner" />
        </div>
        <div class="stats-wrapper" v-if="loaded && games.length && metadata">
            <ul class="stats">
                <li>
                    <strong>Wins:</strong> {{aiView || isAiProfile ? metadata.unrankedAiWins : metadata.wins}}
                </li>
                <li>
                    <strong>Losses:</strong> {{aiView || isAiProfile? metadata.unrankedAiLosses : metadata.losses}}
                </li>
                <li>
                    <strong>Total games:</strong> {{aiView || isAiProfile ? metadata.unrankedAiGames : metadata.games}}
                </li>
                <li>
                    <strong>Win rate:</strong> {{aiView || isAiProfile ? metadata.unrankedAiWinRate : metadata.winRate}}%
                </li>
                <li>
                    <strong>Skill Rating:</strong> <rank :skillRating="metadata.skillRating" /> {{metadata.skillRating}}
                </li>
                <li>
                    <strong>Career High:</strong>&nbsp;
                    <rank :skillRating="metadata.highestSkillRating" /> {{metadata.highestSkillRating}}
                </li>
                <li v-if="metadata.discord">
                    <strong>Discord:</strong>
                    {{renderDiscordUsername(metadata.discord)}}
                </li>
            </ul>
        </div>
		<div class="stats-wrapper" v-if="loaded && games.length && metadata && seasons.length">
			<ul class="stats">
                <li>
					<div class="form-group seasons-wrapper">
						<div class="form-control-wrapper select-season-wrapper select-with-icon-wrapper">
							<select class="form-control select" name="season"  v-model="selectedSeason">
								<option
									:value="season.season.number"
									v-for="(season, i) in seasons"
									:key="`season-${i}`"
								>
									Season {{ season.season.number }}
								</option>
							</select>
							<fa-icon icon="sort-down" class="icon" />
						</div>
						<div class="season-skill-ratings">
							<div class="season-skill-rating">
								<strong>Season Skill Rating:</strong>
								<span class="skill-rating-value">
									<rank :skillRating="selectedSeasonData.skillRating" />
									{{selectedSeasonData.skillRating}}
								</span>
							</div>
							<div class="season-skill-rating">
								<strong>Season High:</strong>
								<span class="skill-rating-value">
									<rank :skillRating="selectedSeasonData.highestSkillRating" />
									{{selectedSeasonData.highestSkillRating}}
								</span>
							</div>
						</div>
					</div>
                </li>
				<li>
					<button
						class="btn btn-small btn-3d stats-btn"
						:class="{
							'btn-primary': !showPerformanceStats,
							'btn-light': showPerformanceStats
						}"
						@click="toggleShowPerformanceStats"
					>
						<icon class="stats-icon" icon="stats" />
					</button>
				</li>
			</ul>
		</div>
        <performance-chart
            :performanceStats="performanceStats"
            :performanceStatsLoaded="performanceStatsLoaded"
            @days-changed="onPerformanceChartDaysChanged"
			v-if="showPerformanceStats"
        />
        <div class="filters-wrapper" v-if="loaded && userId">
            <opponent-search-bar
                :user-uuid="userId"
                :callback="onSelectOpponentCallback"
                :selected-opponent="selectedOpponent"
            />
            <!-- <div v-if="currentUser === 'SpruceGoose'"
                class="unit-types-filter-wrapper"
                :class="{['expanded']: unitTypesFilterExpanded}"
            >
                <div
                    class="unit-types-filter-title"
                    @click="onToggleUnitTypesFilter"
                >
                    <fa-icon icon="chevron-right" class="unit-types-filter-toggle"  v-if="!unitTypesFilterExpanded"/>
                    <fa-icon icon="chevron-down" class="unit-types-filter-toggle"  v-if="unitTypesFilterExpanded"/>
                    <span class="title-text">Units</span>
                    <span class="filter-selections" v-if="selectedUnitTypes.length">
                        ({{selectedUnitTypes.length}} selected)
                    </span>
                </div>
                <div class="unit-types-filter-content">
                    <unit-types-selector
                        :unitTypes="unitTypes.map(unitType => unitType.type)"
                        :selectedUnitTypes="selectedUnitTypes.map(unitType => unitType.type)"
                        :nobilityExpansion="true"
                        :siegeExpansion="true"
                        :onChange="onChangeUnitTypes"
                        :markRoyalGuards="true"
                        :onToggleSameTeam="toggleUnitsOnSameTeam"
                        :unitsOnSameTeam="unitsOnSameTeam"
                    />
                </div>
            </div> -->
            <button class="btn btn-primary btn-3d" @click="onSubmitFilters">
                Search
            </button>
            <button class="btn btn-muted btn-3d" @click="onClearFilters">
                Clear
            </button>
			<div
				v-if="loaded && !isAiProfile"
				class="ai-view-btn"
				@click="(event) => setAiView(event)"
				type="button"
			>
				<span class="btn-text" v-if="aiView">
					Hide AI Games
				</span>
				<span class="btn-text" v-else>
					Show AI Games
				</span>
				<switch-toggle
					:icon="aiView ? '' : ''"
					:checked="aiView"
					:callback="() => {}"
					:altColors="true"
				/>
			</div>
        </div>

        <div class="loading-matches" v-if="!loaded">
            Loading matches<span class="ellipsis animated-ellipsis"></span>
        </div>
        <table class="matches-table" v-if="loaded && games.length">
            <thead>
                <tr class="table-header">
                    <th class="hide-on-mobile">
                        Date
                    </th>
                    <th class="hide-on-mobile hide-on-medium" colspan="2">
                        Room Name
                    </th>
                    <th>
                        Players
                    </th>
                    <th class="text-center hide-on-mobile">
                        Score
                    </th>
                    <th class="text-center">
                        Result
                    </th>
                    <th class="text-center hide-on-medium">
                        SR
                    </th>
                    <th class="hide-on-mobile"></th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="game in games" :key="`game-${game.id}`">
                    <td class="match-date hide-on-mobile" >
                        {{formatDate(game.createdAt)}}
                    </td>
                    <td class="ranked-cell hide-on-mobile hide-on-medium">
                        <span class="ranked-icon-wrapper" v-if="game.ranked" >
                            <fa-icon icon="trophy" class="ranked-icon ranked" />
                        </span>
                        <span class="ranked-icon-wrapper" v-if="!game.ranked" >
                            <fa-icon icon="smile" class="ranked-icon unranked" />
                        </span>
                    </td>
                    <td class="room-name-cell hide-on-mobile hide-on-medium">
                        {{game.roomName}}
                    </td>
                    <td class="teams-wrapper">
                        <div class="teammates">
                            <div class="match-player">
                                <div class="username">
                                    <span class="team-color">
                                        <royal-coin
                                            class-name="royal-coin"
                                            :color="getPlayer(game, userId).team.color"
                                        />
                                    </span>
                                    {{formatUsername(getPlayer(game, userId).user.username)}}
                                </div>
                                <div class="player-units">
                                    <img
                                        class="unit-coin"
                                        :src="getAssetUrl(`@/assets/units/${unitType.imageName}`)"
                                        v-for="(unitType) in getUnitTypes(getPlayer(game, userId).units)"
                                        :key="`unit-type-${game.id}-${unitType.id}`"
                                    />
                                </div>
                            </div>
                             <div class="match-player" v-if="getTeammate(game, userId)">
                                <div class="username">
                                    <router-link
                                        :to="`/matches/${encodeLabel(getTeammate(game, userId).user.username)}`"
                                        class="link"
                                        target="_blank"
                                    >
                                        <span class="team-color">
                                            <royal-coin
                                                class-name="royal-coin"
                                                :color="getPlayer(game, userId).team.color"
                                            />
                                        </span>
                                        {{formatUsername(getTeammate(game, userId).user.username)}}
                                    </router-link>
                                </div>
                                <div class="player-units">
                                    <img
                                        class="unit-coin"
                                        :src="getAssetUrl(`@/assets/units/${unitType.imageName}`)"
                                        v-for="(unitType) in getUnitTypes(getTeammate(game, userId).units)"
                                        :key="`unit-type-${game.id}-${unitType.id}`"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="teams-separator">
                            VS.
                        </div>
                        <div class="teammates">
                            <div class="match-player"
                                v-for="(opponent, i) in getOpponents(game, userId)" :key="`opponent-${i}`">
                                <div class="username">
                                    <router-link
                                        :to="`/matches/${encodeLabel(opponent.user.username)}`"
                                        class="link"
                                        target="_blank"
                                    >
                                        <span class="team-color">
                                            <royal-coin
                                                class-name="royal-coin"
                                                :color="opponent.team.color"
                                            />
                                        </span>
                                        {{formatUsername(opponent.user.username)}}
                                    </router-link>
                                </div>
                                <div class="player-units">
                                    <img
                                        class="unit-coin"
                                        :src="getAssetUrl(`@/assets/units/${unitType.imageName}`)"
                                        v-for="(unitType) in getUnitTypes(opponent.units)"
                                        :key="`unit-type-${game.id}-${unitType.id}`"
                                    />
                                </div>
                            </div>
                        </div>
                    </td>
                    <td class="text-center team-score-td hide-on-mobile">
                        <span class="team-score-wrapper" v-for="(team, i) in getTeams(game)" :key="`team-${i}`">
                            <span class="team-color">
                                <royal-coin class-name="royal-coin" :color="team.color" />
                            </span>
                            <span class="team-score">{{team.controlledBases.length}}</span>
                        </span>
                    </td>
                    <td
                        class="match-result text-center"
                        :class="{[getResultClass(getPlayer(game, userId), getWinningTeam(game).id, game)]: true}"
                        v-html="getResult(getPlayer(game, userId), getWinningTeam(game).id, game)"
                    >
                    </td>
                    <td
                        class="text-center sr-change-cell hide-on-medium"
                        :class="{[getResultClass(getPlayer(game, userId), getWinningTeam(game).id, game)]: true}"
                    >
                        <fa-icon
                            class="icon sr-change-icon"
                            :icon="isWinner(getPlayer(game, userId), game.winningTeamId) ? 'arrow-up' : 'arrow-down'"
                        />
                        <span class="sr-change-value">{{getSrChange(getPlayer(game, userId))}}</span>
                    </td>
                    <td class="text-center hide-on-mobile">
                        <router-link
                            class="nav-link"
                            :to="{ name: 'game', params: {
                                    id: game.id,
                                    redirect: `/matches${username ? `/${username}` : ''}`
                                }
                            }"
                        >
                            <button class="btn btn-muted btn-3d">
                                View
                            </button>
                        </router-link>
                    </td>
                    <td class="text-center">
                        <router-link
                            :to="{
                                name: 'replay',
                                params: {
                                    id: game.id,
                                    redirect: `matches${username ? `/${username}` : ''}`
                                }
                            }"
                            class="nav-link" v-if="gameHasSnapshots(game)"
                        >
                            <button class="btn btn-primary btn-3d replay-btn">
                                Replay
                            </button>
                            <fa-icon class="icon replay-icon" icon="play" />
                        </router-link>
                        <button class="btn btn-muted btn-3d replay-btn disabled" v-if="!gameHasSnapshots(game)">
                              Replay
                        </button>
                        <fa-icon class="icon replay-icon disabled" icon="play" v-if="!gameHasSnapshots(game)" />
                    </td>
                </tr>
            </tbody>
        </table>
        <div class="matches-placeholder" v-if="loaded && !games.length">
            No matches available.
        </div>
        <paginator v-if="loaded && games.length && metadata"
            :onGoToPageCallback="goToPage"
            :pages="pages"
            :currentPage="currentPage"
        />
         <skill-rating-modal :show="showSkillRatingModal" :onCloseCallback="onCloseSrModal" />
    </div>
</template>

<script>
import { getAssetUrl } from '../../helpers/assets';
import moment from 'moment';
import {
    formatUsername,
    gameHasSnapshots,
} from '../../helpers/general';
import { API_BASE_URL } from '@/helpers/env';
import PerformanceChart from './PerformanceChart.vue';

import {
    UNIT_TYPE_ROYAL_COIN,
    UNIT_TYPE_DECOY_COIN,
    unitTypes,
    UNIT_TYPE_ROYAL_GUARD_NOBILITY,
} from '../Unit/Unit-types';

const extendedUnitTypes = [...unitTypes, UNIT_TYPE_ROYAL_GUARD_NOBILITY];

export default {
    name: 'Matches',
    components: {
        PerformanceChart,
    },
    data() {
        return {
            apiUrl: API_BASE_URL,
            showSkillRatingModal: false,
            unitTypesFilterExpanded: false,
			selectedSeason: null,
			showPerformanceStats: false,
			aiView: false,
        };
    },
    async created() {
        const currentPage = this.currentPage ? this.currentPage : 1;
        const username = this.$route.params.username || this.currentUser;

        const options = this.getOptions(currentPage, username);

        await this.$store.dispatch('setSelectedOpponent', null);
        await this.$store.dispatch('getAllUnitTypes');
        await this.$store.dispatch('getMetadata', username);
        await this.$store.dispatch('getUserSeasons', username);
        await this.$store.dispatch('getPerformanceStats', { username });
        await this.$store.dispatch('getGames', options);
    },
    watch: {
        '$route.params.username': async function refresh(username, prevUsername) {
            const decodedUsername = decodeURIComponent(username);
            const decodedPrevUsername = decodeURIComponent(prevUsername);

            if (decodedUsername !== decodedPrevUsername) {
                this.$store.dispatch('setGamesLoaded', false);

                const currentPage = 1;

                const options = this.getOptions(currentPage, username);

                await this.$store.dispatch('getMetadata', username);
				await this.$store.dispatch('getUserSeasons', username);
                await this.$store.dispatch('getPerformanceStats', { username });
                await this.$store.dispatch('getGames', options);
            }
        },
		seasons: function setSeason(seasons, _prevSeasons) {
			if (seasons?.length) {
				this.selectedSeason = seasons[0].season.number;
			}
		},
        aiView() {
            const username = this.$route.params.username || this.currentUser;
            this.$store.dispatch('getMetadata', username);
            const options = this.getOptions(1, username);
            this.$store.dispatch('getGames', options);
        },
    },
    beforeUnmount() {
        this.$store.dispatch('setGamesLoaded', false);
    },
    computed: {
        currentPage() {
            return this.$store.state.matchesState.currentPage;
        },
        currentUser() {
            return this.$store.state.loginState.username;
        },
        games() {
            return this.$store.state.matchesState.games;
        },
        loginUserId() {
            return this.$store.state.loginState.userId;
        },
        loaded() {
            return this.$store.state.matchesState.loaded;
        },
        metadata() {
            return this.$store.state.matchesState.metadata;
        },
        selectedOpponent() {
            return this.$store.state.matchesState.selectedOpponent;
        },
        selectedUnitTypes() {
            return this.$store.state.matchesState.selectedUnitTypes;
        },
        unitsOnSameTeam() {
            return this.$store.state.matchesState.unitsOnSameTeam;
        },
        pages() {
            return this.$store.state.matchesState.pages;
        },
		seasons() {
			return this.$store.state.matchesState.seasons;
		},
		selectedSeasonData() {
			return this.seasons?.find(season => season.season.number === this.selectedSeason) || {};
		},
        showSrBanner() {
            return this.$store.state.appState.showSrBanner && moment().isBefore(moment('02/28/2021'));
        },
        unitTypes() {
            return this.$store.state.matchesState.unitTypes.filter(unitType =>
                extendedUnitTypes.includes(unitType.type),
            );
        },
        userId() {
            return this.$store.state.matchesState.profileUserId;
        },
		isAiProfile() {
            return this.$store.state.matchesState.aiProfile;
        },
        username() {
            return this.$route.params.username ? decodeURIComponent(this.$route.params.username) : '';
        },
        performanceStats() {
            return this.$store.state.matchesState.performanceStats;
        },
        performanceStatsLoaded() {
            return this.$store.state.matchesState.performanceStatsLoaded;
        },
    },
    methods: {
        getAssetUrl,
        onChangeUnitTypes(selectedUnitTypes) {
            const updatedSelectedUnitTypes = this.unitTypes.filter(unitType => selectedUnitTypes.includes(unitType.type));
            this.$store.dispatch('setSelectedUnitTypes', updatedSelectedUnitTypes);
        },
        onToggleUnitTypesFilter() {
            this.unitTypesFilterExpanded = !this.unitTypesFilterExpanded;
        },
        getWinningTeam(game) {
            return game.teams
                .filter(team => team.id === game.winningTeamId)[0];
        },
        getPlayer(game, userId) {
            return game.players.filter(player => player.user.id === userId)[0];
        },
        getTeammate(game, userId) {
            const player = this.getPlayer(game, userId);
            return game.players.filter(gamePlayer =>
                gamePlayer.user.id !== userId &&
                gamePlayer.team.id === player.team.id,
            )[0];
        },
        getOpponents(game, userId) {
            const currentPlayer = this.getPlayer(game, userId);
            return game.players.filter(player => player.team.id !== currentPlayer.team.id);
        },
        getResult(player, winningTeamId, game) {
            const teams = this.getTeams(game);
            const scores = teams.map(team => team.controlledBases.length);

            let forfeit = game.numOfPlayers === 2 ? !scores.includes(6) : !scores.includes(8);

			const winningTeam = teams.find(team => team.id === winningTeamId);

			if (winningTeam.winningScore && winningTeam.winningScore === winningTeam.controlledBases.length) {
				forfeit = false;
			}

            return (player.team.id === winningTeamId ? 'Win' : 'Loss') + (forfeit ? '<br/> (forfeit)' : '');
        },
        isWinner(player, winningTeamId) {
            return player.team.id === winningTeamId;
        },
        getSrChange(player) {
            return Math.abs(player.nextSkillRating - player.prevSkillRating);
        },
        getResultClass(player, winningTeamId, game) {
            return this.getResult(player, winningTeamId, game).split(/[^A-Za-z]/)[0].toLowerCase();
        },
        getTeams(game) {
            const teams = [];
            const teamIds = [];
            game.players.map((player) => {
                if (!teamIds.includes(player.team.id)) {
                    teamIds.push(player.team.id);
                    teams.push(player.team);
                }
            });
            return teams;
        },
        formatUsername(username) {
            return formatUsername(username);
        },
        formatDate(date) {
            return moment(date).local().format('YYYY/MM/DD H:mm');
        },
        gameHasSnapshots(game) {
            return gameHasSnapshots(game);
        },
        getWins(games) {
            const self = this;
            let player;

            return games.filter((game) => {
                player = self.getPlayer(game, self.userId);
                return player.team.id === game.winningTeamId;
            }).length;
        },
        getLosses(games) {
            const self = this;
            let player;

            return games.filter((game) => {
                player = self.getPlayer(game, self.userId);
                return player.team.id !== game.winningTeamId;
            }).length;
        },
        getWinRate(games) {
            return (this.getWins(games) / games.length * 100).toFixed(2);
        },
        getUnitTypes(units) {
            const unitsByType = {};

            units.map((unit) => {
                if (!unitsByType[unit.unitType.type] &&
                    !unit.unitType.type.startsWith(UNIT_TYPE_ROYAL_COIN) &&
                    !unit.unitType.type.startsWith(UNIT_TYPE_DECOY_COIN)
                ) {
                    unitsByType[unit.unitType.type] = unit.unitType;
                }
            });

            return Object.values(unitsByType);
        },
        getSelectedUnitTypeIds() {
            return this.selectedUnitTypes?.map(unitType => unitType.id) ?? [];
        },
        goToPage(number) {
            const options = this.getOptions(number, this.username);
            this.$store.dispatch('getGames', options);
        },
        renderPageTitle() {
            const { userId, loginUserId, username } = this;

            return userId && userId !== loginUserId && username ?
                `Profile: ${this.formatUsername(username)}` :
                'My Games';
        },
        onClearFilters() {
            this.$store.dispatch('setSelectedOpponent', null);
            this.$store.dispatch('setOpponentUsername', null);
            this.$store.dispatch('setSelectedUnitTypes', []);
            this.$store.dispatch('setUnitsOnSameTeam', false);
            const options = this.getOptions(1, this.username);
            this.$store.dispatch('getGames', options);
        },
        onSelectOpponentCallback(opponent) {
            this.$store.dispatch('setSelectedOpponent', opponent);
        },
        onSubmitFilters() {
            this.unitTypesFilterExpanded = false;
            const options = this.getOptions(1, this.username);
            this.$store.dispatch('getGames', options);
        },
        onShowSkillRatingModal(value) {
            this.showSkillRatingModal = value;
        },
        onCloseSrModal() {
            this.showSkillRatingModal = false;
        },
        onCloseSrBanner() {
            this.$store.dispatch('setShowSrBanner', false);
        },
        encodeLabel(label) {
            return encodeURIComponent(label);
        },
        renderDiscordUsername(discordUsername) {
            if (!discordUsername) {
                return '';
            }

            return typeof discordUsername === 'string' &&
                discordUsername.length &&
                discordUsername.charAt(0) === '@' ?
                discordUsername : `@${discordUsername}`;
        },
        toggleUnitsOnSameTeam() {
            this.$store.dispatch('setUnitsOnSameTeam', !this.unitsOnSameTeam);
        },
        onPerformanceChartDaysChanged(days) {
            const username = this.$route.params.username || this.currentUser;
            this.$store.dispatch('getPerformanceStats', { username, days });
        },
		toggleShowPerformanceStats() {
			this.showPerformanceStats = !this.showPerformanceStats;
		},
        getOptions(page, username) {
            return {
                page,
                username,
                opponentUuid: this.selectedOpponent ? this.selectedOpponent.id : null,
                unitTypeIds: this.getSelectedUnitTypeIds(),
                unitsOnSameTeam: this.unitsOnSameTeam,
                aiView: this.aiView,
            };
        },
        setAiView() {
            this.aiView = !this.aiView;
        },
    },
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

    .matches {
        padding: 25px;
        text-align: center;

        .hide-on-mobile,
        .hide-on-medium  {
            display: none;
        }

        .loading-matches,
        .matches-placeholder {
            text-align: center;
            margin: 15px auto;
        }

        .filters-wrapper {
            display: flex;
            flex-direction: column;
            padding: 15px;
            margin: 15px auto;

            .unit-types-filter-wrapper {
                border-radius: 6px;
                cursor: pointer;
                margin-top: 15px;
                position: relative;
                user-select: none;

                &.expanded {

                    .unit-types-filter-content {
                        position: absolute;
                        display: block;
                    }
                }

                .unit-types-filter-title {
                    background: white;
                    font-size: .9em;
                    padding: 10px 12px;
                    display: flex;
                    color: $sage-brown;
                    border: 1px solid $sage-brown;
                    box-shadow: 0 2px $sage-brown;
                    border-radius: 4px;

                    > * {
                        align-self: center;
                    }

                    .title-text {
                        margin-left: 10px;
                    }

                    .filter-selections {
                        color: $archer;
                        margin-left: 5px;
                    }

                    .unit-types-filter-toggle {
                        font-size: .8em;
                    }
                }

                .unit-types-filter-content {
                    display: none;
                    padding: 10px;
                    z-index: 1;
                }
            }

            .btn {
                font-size: .9em;
                margin-top: 15px;
            }

			.ai-view-btn {
				text-align: center;
				display: inline-flex;
				justify-content: center;
				align-items: center;
				overflow: hidden;
				border: 1px solid $sage-brown;
				border-radius: 4px;
				cursor: pointer;
				font-size: .85rem;
				padding: 2px 4px 2px 12px;
				color: $sage-brown;
				flex-grow: 0;
				user-select: none;
			}
        }

        .toggle-option {
            background: white;
            color: $gray;
            border: none;
            padding: 10px 15px;
            cursor: pointer;
            font-size: 0.9em;
            position: relative;

            &.active {
                background: $green;
                color: white;
            }

            &:not(:last-child)::after {
                content: '';
                position: absolute;
                right: 0;
                top: 50%;
                transform: translateY(-50%);
                width: 1px;
                height: 60%;
                background: $medium-gray;
            }
        }

        .stats-wrapper {
            text-align: center;
            margin-top: 15px;

            .stats {
                list-style-type: none;
                padding: 0;
                margin: 0;

                li {
                    display: inline-block;

                    &:not(:last-of-type) {
                        margin-right: 15px;
                    }
                }
            }
        }

        .overlay {
            background: transparent;
        }

        .matches-table {
            background: rgba(255, 255, 255, .85);
            border: 1px solid $light-gray;
            margin: 15px auto;
            width: 100%;

            th {
                color: $dark-brown;
                font-weight: bold;
            }

            td,
            th {
                padding: 10px 5px;
                vertical-align: middle;
                border-top: 1px solid $light-gray;
            }

            .replay-btn {
                display: none;
            }

            .replay-icon {
                background: $green;
                border-radius: 50%;
                color: white;
                cursor: pointer;
                font-size: .8em;
                margin: 0px 10px;
                padding: 10px;

                &.disabled {
                    background: $light-gray;
                    color: $gray;
                    cursor: not-allowed;
                }
            }

            .text-center {
                text-align: center;
            }

            .team-score-td {
                min-width: 100px;

                .team-score-wrapper {
					display: inline-flex;
					justify-content: center;
					align-items: center;
					gap: 5px;
                    margin-right: 10px;
                }
            }

            .royal-coin {
                height: 20px;
                vertical-align: middle;
                width: 20px;
            }

            .team-score {
                vertical-align: middle;
            }

            .match-date {
                min-width: 130px;
            }

            .match-player {
                text-align: center;

                .royal-coin {
                    margin-right: 7px;
                }

                .username {
                    display: flex;
                    justify-content: center;

                    .team-color {
                        vertical-align: middle;

                        .royal-coin {
                            vertical-align: baseline;
                        }
                    }
                }

                &:not(:last-of-type) {
                    margin-bottom: 7px;
                }

                .link {
                    color: $dark-blue;
                }
            }

            .match-result {

                &.win {
                    color: $archer;
                }

                &.loss {
                    color: $medium-dark-gray;
                }
            }

            .player-units {
                border-radius: 5px;
                line-height: 12px;
                margin-top: 5px;
                padding: 5px;
				display: flex;
				justify-content: center;
				align-items: center;
				gap: 4px;

                .unit-coin {
                    display: inline-block;
                    height: 25px;
                    width: 25px;
                }
            }

            .teams-wrapper {
                display: flex;
                flex-direction: column;
                justify-content: center;

                > * {
                    align-self: center;
                }

                .teams-separator {
                    padding: 15px 20px;
                }
            }

            .room-name-cell {
                padding: 15px 20px 15px 15px;
            }

            .ranked-cell {
                padding: 0 0 0 20px;
            }

            .btn {
                &.btn-3d {

                    &.disabled {
                        opacity: .6;
                        cursor: not-allowed;

                        &:hover {
                            top: 0;
                            box-shadow: 0 3px $medium-gray;
                        }
                    }
                }
            }

            .sr-change-cell {
                min-width: 40px;

                &.win {
                    color: $archer;
                }

                &.loss {
                    color: $medium-dark-gray;
                }

                .sr-change-icon {
                    line-height: 1em;
                    font-size: .8em;
                    vertical-align: middle;
                }

                .sr-change-value {
                    line-height: 1em;
                    vertical-align: middle;
                }
            }
        }

        .ranked-icon-wrapper {
            background: $extra-light-gray-2;
            border: 1px solid $light-gray;
            display: inline-block;
            border-radius: 50%;
            height: 25px;
            width: 25px;
            position: relative;
            vertical-align: middle;
            color: $sage-brown;

            .ranked-icon {
                font-size: .9em;
                position: absolute;
                left: 50%;
                top: 50%;
                transform: translate(-50%, -50%);

                &.ranked {
                    color: $gold;
                }
            }
        }
    }

	.seasons-wrapper {
		display: flex;
		align-items: center;
		justify-content: center;
		flex-wrap: wrap;
	}

	.season-skill-ratings {
		display: flex;

		.season-skill-rating {
			display: flex;
			flex-direction: column;
			margin-left: 15px;

			.skill-rating-value {
				margin-left: 5px;
			}
		}
	}

	.form-group .form-control-wrapper.select-season-wrapper {
		position: relative;
		margin-bottom: 15px;

		.icon {
			position: absolute;
			color: $dark-blue;
			right: 10px;
			top: 9px;
		}
	}

	.select-with-icon-wrapper {

		.select {
			padding-right: 30px;
		}
	}

	.stats-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 5px;

		.stats-icon {
			width: 20px;
			height: 20px;

		}

		&.btn-light {
			fill: $sage-brown;
		}

		&.btn-primary {
			fill: white;
		}
	}

    @media only screen and (min-width: $screen-xs) {

        .matches {

            .filters-wrapper {
                flex-direction: row;
                justify-content: center;
				gap: 15px;

                > * {
                    flex-grow: 1;
                }

                .opponent-search-bar {
                    margin-right: 7.5px;
                    width: calc(50% - 32px);
                }

                .unit-types-filter-wrapper {
                    margin-top: 0;
                    margin-left: 7.5px;
                    width: calc(50% - 32px);
                }

                .btn {
                    align-self: flex-start;
                    margin-top: 0;
                    max-width: 85px;
                }

                .btn-muted {
                    max-width: 85px;
                }
            }

			.form-group .form-control-wrapper.select-season-wrapper  {
				margin-bottom: 0;
			}

			.season-skill-ratings {

				.season-skill-rating {
					flex-direction: row;
				}
			}
        }
    }

    @media only screen and (min-width: $screen-sm) {

        .matches {

            .title-wrapper {

                .title {
                    max-width: 600px;
                }
            }

            .matches-table {
                width: auto;

                .player-units {
                    margin-top: 10px;
                }

                .replay-btn {
                    display: inline-block;
                }

                .replay-icon {
                    display: none;
                }

                .teams-wrapper {
                    flex-direction: row;

                    .teams-separator {
                        padding: 0 20px;
                    }
                }
            }

            .filters-wrapper {
                max-width: 80%;

                .opponent-search-bar {
                    max-width: 350px;
                }

                .unit-types-filter-wrapper {
                    max-width: 400px;
                }
            }

        }
    }

    @media only screen and (min-width: $screen-md) {

        .matches {

            .hide-on-mobile {
                display: table-cell;
            }

            .hide-on-medium {
                display: none;
            }

            .matches-table {

                td,
                th {
                    padding: 15px 20px;
                }
            }

            .matches-pagination {
                margin: 0 10%;
            }
        }
    }

    @media only screen and (min-width: $screen-lg) {

        .matches {

            .hide-on-medium {
                display: table-cell;
            }
        }
    }

</style>
